// frontend/src/components/engineer/pages/DashboardPage.tsx - FIXED VERSION
import React, { useState, useEffect, useMemo } from 'react';
import {
  ClockIcon,
  UserGroupIcon,
  DocumentTextIcon,
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChartBarIcon,
  SparklesIcon,
  ArrowRightIcon,
  PlusIcon,
  EyeIcon
} from '@heroicons/react/24/outline';
import { AuthUser } from '../../../services/auth.service';
import { editOvertimeService, EditableOvertimeRequest } from '../../../services/editOvertime.service';
import { dashboardStatsService, RecentActivity, MonthlyStats, WeeklyTrend } from '../../../services/dashboardStats.service';
import ValidationStatusBadge from '../components/ValidationStatusBadge';
import RecentActivityCard from '../components/RecentActivityCard';
import MonthlyStatsCard from '../components/MonthlyStatsCard';

interface DashboardPageProps {
  user?: AuthUser;
  onNavigate?: (page: string, params?: any) => void;
}

// 🆕 SAFE DATE CONVERSION HELPER
const toSafeDate = (value: any): Date | null => {
  if (!value) return null;
  
  // Already a Date object
  if (value instanceof Date) {
    return value;
  }
  
  // Firestore Timestamp with toDate method
  if (value && typeof value.toDate === 'function') {
    try {
      return value.toDate();
    } catch (e) {
      console.warn('Failed to convert timestamp:', e);
      return null;
    }
  }
  
  // String that can be converted to Date
  if (typeof value === 'string') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Unix timestamp (number)
  if (typeof value === 'number') {
    return new Date(value);
  }
  
  console.warn('Unable to convert to date:', value);
  return null;
};

// 🆕 SAFE VALIDATION STATUS INFO - replaces the imported one
const getValidationStatusInfo = (request: any) => {
  const willValidateOn = toSafeDate(request.willValidateOn);
  const validatedAt = request.attendanceValidation?.validatedAt 
    ? toSafeDate(request.attendanceValidation.validatedAt)
    : null;

  const now = new Date();
  const isValidated = request.attendanceValidation?.isValidated || false;
  const validationPending = request.attendanceValidation?.validationPending || false;

  // If already validated
  if (isValidated && validatedAt) {
    const presentCount = request.attendanceValidation?.presentCount || 0;
    const absentCount = request.attendanceValidation?.absentCount || 0;
    const totalEmployees = request.totalEmployees || request.employeeIds?.length || 0;

    return {
      status: 'validated' as const,
      statusText: 'Validated',
      statusColor: presentCount === totalEmployees ? '#10b981' : '#f59e0b',
      message: `${presentCount}/${totalEmployees} employees present`,
      details: {
        presentCount,
        absentCount,
        totalEmployees,
        validatedAt: validatedAt.toLocaleString()
      }
    };
  }

  // If validation is pending and scheduled
  if (validationPending && willValidateOn) {
    if (now < willValidateOn) {
      // Validation scheduled for future
      const hoursUntil = Math.round((willValidateOn.getTime() - now.getTime()) / (1000 * 60 * 60));
      
      return {
        status: 'scheduled' as const,
        statusText: 'Validation Scheduled',
        statusColor: '#3b82f6',
        message: hoursUntil > 0 
          ? `Validates in ${hoursUntil} hours`
          : 'Validates soon',
        details: {
          scheduledFor: willValidateOn.toLocaleString(),
          hoursUntil
        }
      };
    } else {
      // Validation time has passed but not yet validated
      return {
        status: 'pending' as const,
        statusText: 'Validation Pending',
        statusColor: '#f59e0b',
        message: 'Awaiting automatic validation',
        details: {
          scheduledFor: willValidateOn.toLocaleString()
        }
      };
    }
  }

  // Default case - no validation info
  return {
    status: 'unknown' as const,
    statusText: 'Unknown',
    statusColor: '#6b7280',
    message: 'No validation information',
    details: {}
  };
};

// 🆕 SAFE VALIDATION PENDING CHECK - replaces the imported one
const isValidationPending = (request: any): boolean => {
  if (!request.attendanceValidation) return false;
  
  const isValidated = request.attendanceValidation.isValidated || false;
  const validationPending = request.attendanceValidation.validationPending || false;
  
  return !isValidated && validationPending;
};

const DashboardPage: React.FC<DashboardPageProps> = ({ user, onNavigate }) => {
  const [todayRequests, setTodayRequests] = useState<EditableOvertimeRequest[]>([]);
  const [upcomingRequests, setUpcomingRequests] = useState<EditableOvertimeRequest[]>([]);
  const [recentlyValidated, setRecentlyValidated] = useState<EditableOvertimeRequest[]>([]);
  const [recentActivity, setRecentActivity] = useState<RecentActivity[]>([]);
  const [monthlyStats, setMonthlyStats] = useState<MonthlyStats | null>(null);
  const [weeklyTrend, setWeeklyTrend] = useState<WeeklyTrend[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const userId = useMemo(() => {
    const id = user?.employeeNo || user?.employeeNumber || user?.uid || (user as any)?.id || '';
    console.log('🔍 DEBUG: Using userId:', id);
    return id;
  }, [user]);

  useEffect(() => {
    if (userId) {
      loadDashboardData();
    }
  }, [userId]);

  const loadDashboardData = async () => {
    if (!userId) {
      console.warn('⚠️ No userId available, skipping dashboard data load');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      console.log('📊 Loading dashboard data for userId:', userId);

      const [today, upcoming, validated, activity, stats, trend] = await Promise.all([
        editOvertimeService.getTodayOvertimeRequests(userId).catch(e => {
          console.error('Error loading today requests:', e);
          return [];
        }),
        editOvertimeService.getUpcomingOvertimeRequests(userId).catch(e => {
          console.error('Error loading upcoming requests:', e);
          return [];
        }),
        editOvertimeService.getValidatedOvertimeRequests(userId, 7).catch(e => {
          console.error('Error loading validated requests:', e);
          return [];
        }),
        dashboardStatsService.getRecentActivity(userId, 8).catch(e => {
          console.error('Error loading recent activity:', e);
          return [];
        }),
        dashboardStatsService.getMonthlyStats(userId).catch(e => {
          console.error('Error loading monthly stats:', e);
          return null;
        }),
        dashboardStatsService.getWeeklyTrend(userId).catch(e => {
          console.error('Error loading weekly trend:', e);
          return [];
        })
      ]);

      setTodayRequests(today);
      setUpcomingRequests(upcoming);
      setRecentlyValidated(validated);
      setRecentActivity(activity);
      setMonthlyStats(stats);
      setWeeklyTrend(trend);

      console.log(`✅ Dashboard loaded: ${today.length} today, ${upcoming.length} upcoming, ${validated.length} validated, ${activity.length} activities`);
    } catch (error) {
      console.error('❌ Error loading dashboard:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const stats = useMemo(() => ({
    todayTotal: todayRequests.length,
    todayPending: todayRequests.filter(r => r.status === 'pending').length,
    todayApproved: todayRequests.filter(r => r.status === 'approved').length,
    upcomingTotal: upcomingRequests.length,
    pendingValidation: todayRequests.filter(r => isValidationPending(r)).length,
    validatedWithIssues: todayRequests.filter(r => {
      const info = getValidationStatusInfo(r);
      return info.status === 'validated' && (r.attendanceValidation?.absentCount ?? 0) > 0;
    }).length,
    recentValidationsCount: recentlyValidated.length,
    recentValidationsWithIssues: recentlyValidated.filter(r => {
      const validation = r.attendanceValidation;
      return validation && (validation.absentCount ?? 0) > 0;
    }).length
  }), [todayRequests, upcomingRequests, recentlyValidated]);

  const currentDate = new Date();
  const greeting = currentDate.getHours() < 12 ? 'Good Morning' : 
                   currentDate.getHours() < 18 ? 'Good Afternoon' : 'Good Evening';

  const maxTrendValue = useMemo(() => {
    return Math.max(...weeklyTrend.map(t => t.requests), 1);
  }, [weeklyTrend]);

  if (isLoading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '60vh',
        background: 'linear-gradient(135deg, #f7f5f2 0%, #faf9f7 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '4rem',
            height: '4rem',
            border: '4px solid #f3f4f6',
            borderTop: '4px solid #c29e5d',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 1.5rem'
          }}></div>
          <p style={{ color: '#6b7280', fontSize: '1rem', fontWeight: '500' }}>
            Loading your dashboard...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f8f9fa 0%, #f0f2f5 100%)',
      padding: '1.5rem'
    }}>
      <div style={{ maxWidth: '100rem', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '1.5rem',
          padding: '2rem 2.5rem',
          marginBottom: '1.5rem',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #f0f0f0',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(90deg, #c29e5d, #d4b373, #e5c68c)',
            borderRadius: '1.5rem 1.5rem 0 0'
          }}/>
          <div style={{
            position: 'absolute',
            top: '-100px',
            right: '-100px',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(194, 158, 93, 0.08) 0%, transparent 70%)',
            borderRadius: '50%'
          }}/>

          <div style={{ position: 'relative', zIndex: 1 }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '1.5rem'
            }}>
              <div>
                <div style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  background: 'linear-gradient(135deg, rgba(194, 158, 93, 0.15) 0%, rgba(194, 158, 93, 0.05) 100%)',
                  padding: '0.375rem 0.875rem',
                  borderRadius: '2rem',
                  marginBottom: '0.75rem',
                  border: '1px solid rgba(194, 158, 93, 0.2)'
                }}>
                  <SparklesIcon style={{ width: '1rem', height: '1rem', color: '#c29e5d' }} />
                  <span style={{ color: '#c29e5d', fontSize: '0.8125rem', fontWeight: '600' }}>
                    {greeting}
                  </span>
                </div>
                <h1 style={{
                  fontSize: '2rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: '0 0 0.375rem 0',
                  letterSpacing: '-0.02em'
                }}>
                  Welcome back, {user?.name?.split(' ')[0] || 'Engineer'}!
                </h1>
                <p style={{
                  fontSize: '0.9375rem',
                  color: '#6b7280',
                  margin: 0
                }}>
                  {currentDate.toLocaleDateString('en-US', {
                    weekday: 'long',
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </p>
              </div>

              <button
                onClick={() => onNavigate?.('create-overtime')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.875rem 1.5rem',
                  background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.75rem',
                  fontWeight: '600',
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(194, 158, 93, 0.35)',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 6px 20px rgba(194, 158, 93, 0.45)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(194, 158, 93, 0.35)';
                }}
              >
                <PlusIcon style={{ width: '1.125rem', height: '1.125rem' }} />
                New Overtime
              </button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '1rem',
          marginBottom: '1.5rem'
        }}>
          {/* Today's Overtime */}
          <div 
            onClick={() => onNavigate?.('reports-history')}
            style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '1.5rem',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f0f0f0',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.06)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: '#6b7280', fontWeight: '500' }}>
                  Today's Overtime
                </p>
                <p style={{ margin: '0 0 0.5rem', fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
                  {stats.todayTotal}
                </p>
                <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem' }}>
                  <span style={{ color: '#f59e0b', fontWeight: '600' }}>⏳ {stats.todayPending}</span>
                  <span style={{ color: '#10b981', fontWeight: '600' }}>✓ {stats.todayApproved}</span>
                </div>
              </div>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}>
                <CalendarDaysIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
              </div>
            </div>
          </div>

          {/* Pending Validation */}
          <div style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: '#6b7280', fontWeight: '500' }}>
                  Pending Validation
                </p>
                <p style={{ margin: '0 0 0.5rem', fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
                  {stats.pendingValidation}
                </p>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#f59e0b', fontWeight: '600' }}>
                  Tomorrow 6 AM
                </p>
              </div>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <ClockIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
              </div>
            </div>
          </div>

          {/* Validation Issues */}
          <div style={{
            background: 'white',
            borderRadius: '1rem',
            padding: '1.5rem',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
            border: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: '#6b7280', fontWeight: '500' }}>
                  Validation Issues
                </p>
                <p style={{ margin: '0 0 0.5rem', fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
                  {stats.validatedWithIssues}
                </p>
                <p style={{
                  margin: 0,
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  color: stats.validatedWithIssues > 0 ? '#ef4444' : '#10b981'
                }}>
                  {stats.validatedWithIssues > 0 ? '⚠️ Action needed' : '✅ All clear'}
                </p>
              </div>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: stats.validatedWithIssues > 0
                  ? 'linear-gradient(135deg, #ef4444, #dc2626)'
                  : 'linear-gradient(135deg, #10b981, #059669)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {stats.validatedWithIssues > 0 ? (
                  <ExclamationTriangleIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
                ) : (
                  <CheckCircleIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
                )}
              </div>
            </div>
          </div>

          {/* Upcoming */}
          <div 
            onClick={() => onNavigate?.('reports-history')}
            style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '1.5rem',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f0f0f0',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.06)';
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
              <div>
                <p style={{ margin: '0 0 0.5rem', fontSize: '0.8125rem', color: '#6b7280', fontWeight: '500' }}>
                  Upcoming (7 days)
                </p>
                <p style={{ margin: '0 0 0.5rem', fontSize: '2rem', fontWeight: '700', color: '#1f2937' }}>
                  {stats.upcomingTotal}
                </p>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#6b7280', fontWeight: '500' }}>
                  Scheduled requests
                </p>
              </div>
              <div style={{
                width: '3rem',
                height: '3rem',
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                borderRadius: '0.75rem',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <DocumentTextIcon style={{ width: '1.5rem', height: '1.5rem', color: 'white' }} />
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 380px',
          gap: '1.5rem'
        }}>
          
          {/* Left Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Today's Requests */}
            <div style={{
              background: 'white',
              borderRadius: '1rem',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f0f0f0',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '1.25rem 1.5rem',
                borderBottom: '1px solid #f3f4f6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    Today's Overtime
                  </h2>
                  <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                    {todayRequests.length} request{todayRequests.length !== 1 ? 's' : ''} scheduled
                  </p>
                </div>
                <button
                  onClick={() => onNavigate?.('reports-history')}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.5rem 0.875rem',
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    color: '#374151',
                    fontSize: '0.8125rem',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                  }}
                >
                  <EyeIcon style={{ width: '1rem', height: '1rem' }} />
                  View All
                </button>
              </div>

              <div style={{ padding: '1rem 1.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                {todayRequests.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {todayRequests.map(request => (
                      <div
                        key={request.id}
                        onClick={() => onNavigate?.('edit-overtime', request.id)}
                        style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.75rem',
                          padding: '1rem 1.25rem',
                          background: '#fafbfc',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = '#f3f4f6';
                          e.currentTarget.style.borderColor = '#c29e5d';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = '#fafbfc';
                          e.currentTarget.style.borderColor = '#e5e7eb';
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                          <div>
                            <h3 style={{ margin: '0 0 0.375rem', fontSize: '0.9375rem', fontWeight: '600', color: '#1f2937' }}>
                              {request.projectName}
                            </h3>
                            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.8125rem', color: '#6b7280' }}>
                              <span>📋 {request.projectCode}</span>
                              <span>👥 {request.totalEmployees}</span>
                            </div>
                          </div>
                          <span style={{
                            padding: '0.25rem 0.625rem',
                            borderRadius: '0.5rem',
                            fontSize: '0.6875rem',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            background: request.status === 'approved' ? '#d1fae5' : 
                                       request.status === 'rejected' ? '#fee2e2' : '#fef3c7',
                            color: request.status === 'approved' ? '#047857' : 
                                   request.status === 'rejected' ? '#dc2626' : '#d97706'
                          }}>
                            {request.status}
                          </span>
                        </div>
                        <ValidationStatusBadge request={request} showDetails={true} size="sm" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
                    <CalendarDaysIcon style={{ width: '3rem', height: '3rem', color: '#d1d5db', margin: '0 auto 0.75rem' }} />
                    <p style={{ margin: '0 0 0.375rem', fontSize: '0.9375rem', fontWeight: '500', color: '#6b7280' }}>
                      No overtime today
                    </p>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#9ca3af' }}>
                      Create a new request to get started
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Recent Validations Section */}
            <div style={{
              background: 'white',
              borderRadius: '1rem',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f0f0f0',
              overflow: 'hidden'
            }}>
              <div style={{
                padding: '1.25rem 1.5rem',
                borderBottom: '1px solid #f3f4f6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)'
              }}>
                <div>
                  <h2 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    Recent Validations
                  </h2>
                  <p style={{ fontSize: '0.8125rem', color: '#6b7280', margin: '0.25rem 0 0' }}>
                    Last 7 days attendance validation results
                  </p>
                </div>
                <div style={{
                  width: '2.5rem',
                  height: '2.5rem',
                  background: 'linear-gradient(135deg, #10b981, #059669)',
                  borderRadius: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)'
                }}>
                  <CheckCircleIcon style={{ width: '1.25rem', height: '1.25rem', color: 'white' }} />
                </div>
              </div>

              <div style={{ padding: '1rem 1.5rem', maxHeight: '400px', overflowY: 'auto' }}>
                {recentlyValidated.length > 0 ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {recentlyValidated.map(request => {
                      const validation = request.attendanceValidation;
                      if (!validation || !validation.isValidated) return null;

                      const allPresent = validation.absentCount === 0;
                      const allAbsent = validation.presentCount === 0;
                      const partial = validation.presentCount > 0 && validation.absentCount > 0;
                      
                      // 🆕 SAFE DATE CONVERSION
                      const overtimeDate = toSafeDate(request.overtimeDate);
                      const validatedAtDate = toSafeDate(validation.validatedAt);
                      
                      return (
                        <div
                          key={request.id}
                          onClick={() => onNavigate?.('edit-overtime', request.id)}
                          style={{
                            border: `2px solid ${allPresent ? '#10b981' : partial ? '#f59e0b' : '#ef4444'}`,
                            borderRadius: '0.75rem',
                            padding: '1rem 1.25rem',
                            background: allPresent ? '#f0fdf4' : partial ? '#fef3c7' : '#fee2e2',
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateX(4px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateX(0)';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                            <div style={{ flex: 1 }}>
                              <h3 style={{ margin: '0 0 0.375rem', fontSize: '0.9375rem', fontWeight: '600', color: '#1f2937' }}>
                                {request.projectName}
                              </h3>
                              <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                                <span>📅 {overtimeDate ? overtimeDate.toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric'
                                }) : 'N/A'}</span>
                                <span>⏰ Validated {validatedAtDate ? validatedAtDate.toLocaleString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                }) : 'Recently'}</span>
                              </div>
                              
                              {/* Status Badges */}
                              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  background: allPresent ? '#10b981' : partial ? '#f59e0b' : '#ef4444',
                                  color: 'white',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.6875rem',
                                  fontWeight: '700',
                                  textTransform: 'uppercase',
                                  boxShadow: `0 2px 4px ${allPresent ? 'rgba(16, 185, 129, 0.3)' : partial ? 'rgba(245, 158, 11, 0.3)' : 'rgba(239, 68, 68, 0.3)'}`
                                }}>
                                  {allPresent ? '✅ All Present' : allAbsent ? '❌ All Absent' : '⚠️ Partial'}
                                </span>
                                <span style={{
                                  padding: '0.25rem 0.75rem',
                                  background: 'white',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '0.375rem',
                                  fontSize: '0.6875rem',
                                  fontWeight: '600',
                                  color: '#059669'
                                }}>
                                  {validation.presentCount} Present
                                </span>
                                {validation.absentCount > 0 && (
                                  <span style={{
                                    padding: '0.25rem 0.75rem',
                                    background: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '0.375rem',
                                    fontSize: '0.6875rem',
                                    fontWeight: '600',
                                    color: '#dc2626'
                                  }}>
                                    {validation.absentCount} Absent
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          
                          {/* Show absent employees if any */}
                          {validation.absentCount > 0 && (
                            <div style={{
                              marginTop: '0.75rem',
                              padding: '0.75rem',
                              background: 'white',
                              borderRadius: '0.5rem',
                              border: '1px solid rgba(0,0,0,0.05)'
                            }}>
                              <div style={{ fontSize: '0.6875rem', fontWeight: '700', color: '#dc2626', marginBottom: '0.375rem', textTransform: 'uppercase', letterSpacing: '0.025em' }}>
                                Absent Employees:
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', lineHeight: '1.5' }}>
                                {validation.absentEmployeeNames.slice(0, 3).join(', ')}
                                {validation.absentEmployeeNames.length > 3 && (
                                  <span style={{ fontWeight: '600', color: '#dc2626' }}>
                                    {' '}+{validation.absentEmployeeNames.length - 3} more
                                  </span>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
                    <CheckCircleIcon style={{ width: '3rem', height: '3rem', color: '#d1d5db', margin: '0 auto 0.75rem' }} />
                    <p style={{ margin: '0 0 0.375rem', fontSize: '0.9375rem', fontWeight: '500', color: '#6b7280' }}>
                      No validations yet
                    </p>
                    <p style={{ margin: 0, fontSize: '0.8125rem', color: '#9ca3af' }}>
                      Validation results will appear here after 6 AM
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Weekly Chart */}
            <div style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '1.5rem',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f0f0f0'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                <div>
                  <h3 style={{ margin: '0 0 0.25rem', fontSize: '1rem', fontWeight: '600', color: '#1f2937' }}>
                    Weekly Overview
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.8125rem', color: '#6b7280' }}>
                    Last 7 days activity
                  </p>
                </div>
                <ChartBarIcon style={{ width: '1.5rem', height: '1.5rem', color: '#c29e5d' }} />
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '120px', gap: '0.5rem' }}>
                {weeklyTrend.map((day, index) => (
                  <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{
                      width: '100%',
                      maxWidth: '40px',
                      height: `${Math.max(8, (day.requests / maxTrendValue) * 100)}px`,
                      background: day.requests > 0 
                        ? 'linear-gradient(180deg, #c29e5d, #d4b373)'
                        : '#e5e7eb',
                      borderRadius: '0.375rem 0.375rem 0 0',
                      position: 'relative',
                      transition: 'all 0.3s'
                    }}>
                      {day.requests > 0 && (
                        <span style={{
                          position: 'absolute',
                          top: '-1.5rem',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#c29e5d'
                        }}>
                          {day.requests}
                        </span>
                      )}
                    </div>
                    <span style={{ marginTop: '0.5rem', fontSize: '0.6875rem', color: '#6b7280', fontWeight: '500' }}>
                      {day.day}
                    </span>
                  </div>
                ))}
              </div>

              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: '1.5rem',
                marginTop: '1rem',
                paddingTop: '1rem',
                borderTop: '1px solid #f3f4f6'
              }}>
                <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                  Total: {weeklyTrend.reduce((sum, d) => sum + d.requests, 0)} requests
                </span>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            
            {/* Recent Activity */}
            <RecentActivityCard 
              activities={recentActivity}
              onActivityClick={(id) => {
                if (id === 'reports-history') {
                  onNavigate?.('reports-history');
                } else {
                  onNavigate?.('edit-overtime', id);
                }
              }}
            />

            {/* Monthly Stats */}
            <MonthlyStatsCard stats={monthlyStats} />

            {/* Quick Actions */}
            <div style={{
              background: 'white',
              borderRadius: '1rem',
              padding: '1.25rem',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid #f0f0f0'
            }}>
              <h3 style={{ margin: '0 0 1rem', fontSize: '0.9375rem', fontWeight: '600', color: '#1f2937' }}>
                Quick Actions
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <button
                  onClick={() => onNavigate?.('create-overtime')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: 'linear-gradient(135deg, #c29e5d, #d4b373)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.625rem',
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(194, 158, 93, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <UserGroupIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                  Create Overtime
                  <ArrowRightIcon style={{ width: '1rem', height: '1rem', marginLeft: 'auto' }} />
                </button>

                <button
                  onClick={() => onNavigate?.('reports-history')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#f9fafb',
                    color: '#374151',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.625rem',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                  }}
                >
                  <DocumentTextIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                  View Reports
                  <ArrowRightIcon style={{ width: '1rem', height: '1rem', marginLeft: 'auto', opacity: 0.5 }} />
                </button>

                <button
                  onClick={() => onNavigate?.('view-timesheets')}
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    background: '#f9fafb',
                    color: '#374151',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.625rem',
                    fontWeight: '500',
                    fontSize: '0.875rem',
                    cursor: 'pointer',
                    textAlign: 'left',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.625rem',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = '#f3f4f6';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = '#f9fafb';
                  }}
                >
                  <ChartBarIcon style={{ width: '1.25rem', height: '1.25rem' }} />
                  Timesheets
                  <ArrowRightIcon style={{ width: '1rem', height: '1rem', marginLeft: 'auto', opacity: 0.5 }} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default DashboardPage;